import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';

import LandingSection from '../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../MyComponents/LandingSections/SectionEyebrow';
import HighlightMark from '../../MyComponents/LandingSections/HighlightMark';
import { CONTACT_US_LINK, HOME_LINK } from '../../common/urlLinks';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

const useStyles = makeStyles((theme) => ({
    intro: {
        maxWidth: '720px',
        margin: '0 auto',
        textAlign: 'center',
    },
    eyebrowRow: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '22px',
    },
    title: {
        margin: '0 0 16px',
        fontSize: 'clamp(2rem, 4.4vw, 3.4rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.035em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        textWrap: 'balance',
    },
    lead: {
        maxWidth: '560px',
        margin: '0 auto 26px',
        color: REDESIGN_COLORS.body,
        fontSize: '1.06rem',
        lineHeight: 1.6,
    },
    ctaRow: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '12px',
    },
    cta: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        minHeight: '52px',
        padding: '0 26px',
        borderRadius: '999px',
        fontSize: '1rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        textDecoration: 'none',
    },
    ctaPrimary: {
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.primary,
        boxShadow: '0 4px 12px rgba(0, 124, 134, 0.24)',
        '&:hover': {
            color: REDESIGN_COLORS.white,
            background: REDESIGN_COLORS.primaryHover,
        },
    },
    ctaSecondary: {
        border: `1px solid ${REDESIGN_COLORS.line}`,
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.white,
        '&:hover': {
            borderColor: REDESIGN_COLORS.primary,
            color: REDESIGN_COLORS.link,
        },
    },
    // The visual: an empty board drawn in CSS, on the same mint mat and white
    // frame the landing's screenshots use. Nothing to load, and it reads as
    // "the board is blank" at any size.
    figure: {
        boxSizing: 'border-box',
        maxWidth: '760px',
        margin: '44px auto 0',
        padding: 'clamp(12px, 1.8vw, 22px)',
        borderRadius: '24px',
        background: REDESIGN_COLORS.mist,
        [theme.breakpoints.down('xs')]: {
            marginTop: '30px',
        },
    },
    frame: {
        overflow: 'hidden',
        borderRadius: '14px',
        background: REDESIGN_COLORS.white,
        boxShadow: '0 1px 2px rgba(6, 63, 69, 0.06), 0 16px 40px rgba(6, 63, 69, 0.12)',
    },
    chrome: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 14px',
        borderBottom: `1px solid ${REDESIGN_COLORS.boardDivider}`,
    },
    dot: {
        width: '9px',
        height: '9px',
        borderRadius: '50%',
        background: REDESIGN_COLORS.sand,
    },
    tab: {
        marginLeft: '10px',
        padding: '3px 10px',
        borderRadius: '999px',
        background: REDESIGN_COLORS.cream,
        color: REDESIGN_COLORS.muted,
        fontSize: '0.68rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
    },
    canvas: {
        position: 'relative',
        display: 'grid',
        placeItems: 'center',
        aspectRatio: '16 / 8',
        // The ruled lines of a lined board, faint.
        backgroundImage: `repeating-linear-gradient(to bottom, transparent 0 31px, ${REDESIGN_COLORS.boardDivider} 31px 32px)`,
        backgroundPosition: '0 14px',
    },
    scribble: {
        textAlign: 'center',
    },
    code: {
        display: 'block',
        margin: 0,
        fontFamily: REDESIGN_FONTS.accent,
        fontSize: 'clamp(5rem, 16vw, 8.5rem)',
        lineHeight: 0.9,
        fontWeight: 700,
        color: REDESIGN_COLORS.tealInk,
        transform: 'rotate(-3deg)',
    },
    note: {
        display: 'inline-block',
        marginTop: '14px',
        fontFamily: REDESIGN_FONTS.accent,
        fontSize: 'clamp(1.3rem, 3vw, 1.8rem)',
        color: REDESIGN_COLORS.accent,
        transform: 'rotate(1.5deg)',
    },
    path: {
        margin: '22px auto 0',
        maxWidth: '100%',
        color: REDESIGN_COLORS.muted,
        fontSize: '0.84rem',
        textAlign: 'center',
        overflowWrap: 'anywhere',
        '& code': {
            padding: '2px 8px',
            borderRadius: '8px',
            background: REDESIGN_COLORS.white,
            border: `1px solid ${REDESIGN_COLORS.sand}`,
            color: REDESIGN_COLORS.body,
            fontSize: '0.82rem',
        },
    },
}));

/**
 * The 404 page, on the landing's hero pattern: eyebrow, headline, lead, two
 * pill CTAs, and a blank board with a handwritten "404" where a screenshot
 * would go. Rendered both signed out and inside the app drawer.
 */
const NotFound = () => {
    const classes = useStyles();
    const { pathname } = useLocation();

    return (
        <LandingSection id="top" maxWidth={1080} paddingTop={46} paddingBottom={56}>
            <div className={classes.intro}>
                <div className={classes.eyebrowRow}>
                    <SectionEyebrow>
                        <FormattedMessage id="redesign.notFound.eyebrow" />
                    </SectionEyebrow>
                </div>
                <h1 className={classes.title}>
                    <FormattedMessage
                        id="redesign.notFound.title"
                        values={{ mark: (chunks) => <HighlightMark>{chunks}</HighlightMark> }}
                    />
                </h1>
                <p className={classes.lead}>
                    <FormattedMessage id="redesign.notFound.lead" />
                </p>
                <div className={classes.ctaRow}>
                    <Link to={HOME_LINK} className={`${classes.cta} ${classes.ctaPrimary}`}>
                        <FormattedMessage id="redesign.notFound.ctaHome" />
                        <span aria-hidden="true">→</span>
                    </Link>
                    <Link to={CONTACT_US_LINK} className={`${classes.cta} ${classes.ctaSecondary}`}>
                        <FormattedMessage id="contactus" />
                    </Link>
                </div>
            </div>

            <div className={classes.figure} aria-hidden="true">
                <div className={classes.frame}>
                    <div className={classes.chrome}>
                        <span className={classes.dot} />
                        <span className={classes.dot} />
                        <span className={classes.dot} />
                        <span className={classes.tab}>VBoard</span>
                    </div>
                    <div className={classes.canvas}>
                        <div className={classes.scribble}>
                            <span className={classes.code}>404</span>
                            <span className={classes.note}>
                                <FormattedMessage id="redesign.notFound.boardNote" />
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <p className={classes.path}>
                <FormattedMessage id="redesign.notFound.path" values={{ path: <code>{pathname}</code> }} />
            </p>
        </LandingSection>
    );
};

export default NotFound;
